import { useEffect, useMemo, useRef, useState } from 'react';
import { FileText, FolderOpen, Upload } from 'lucide-react';
import type { UploadLinkConfig } from '@peles/shared';
import type { ThreadReportRequest } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { FilePickerModal } from '../files/FilePickerModal';
import { UploadLinkFields } from './UploadLinkOptions';
import { ThreadReportModal } from './ThreadReportModal';
import { MessageComposer } from './MessageComposer';
import type { ThreadContext, ThreadFolderOption } from '../../api/threadTools';
import { uploadRidesNextMessage, type ConversationToolIntent } from './conversationTools';

/**
 * The three things a conversation can carry beyond words
 * (COMMUNICATIONS-DESIGN H4).
 *
 * ⚠️ **It reports a choice and never applies one.** That is what lets the same
 * control serve an open conversation and one being written: the parent decides
 * whether the choice happens now or when the thread exists. A toolbar that knew
 * about `threadId` could only ever serve the first, which is how starting a
 * conversation ended up with none of these.
 *
 * ⚠️ **Beside `<ThreadPanel>`, not a fork of it.** The package's panel owns the
 * transcript, the participants, the status and the composer, and its dropzone
 * already takes a file off the device. What it has no seam for is *this*
 * application's three answers to "attach something": a file already filed under
 * the event, a report rendered on demand, and a request for a file back. Those
 * are domain controls, so they are ours.
 */
export interface ConversationToolbarProps {
  /**
   * Where this conversation's files live.
   *
   * From `GET /threads/:id/context` for an open one and
   * `GET /threads/record-context` for one being started — the same answer, the
   * same server-side walk, keyed differently because there is not yet a thread
   * to key it by.
   */
  context: ThreadContext | null;
  /** What was just chosen. Applied now, or held until there is a thread. */
  onIntent: (intent: ConversationToolIntent) => void | Promise<void>;
  disabled?: boolean;
  /**
   * Whether the conversation exists yet.
   *
   * Only wording depends on it, but the wording matters: "everybody on this
   * conversation gets a link" is a promise about people who have not been
   * chosen yet when the conversation is still being written.
   */
  pending?: boolean;
  /**
   * Whether the parent can hold a file request for the next message.
   *
   * ⚠️ **Only the wording depends on it, and the wording was the bug.** The
   * parent decides the timing from `uploadRidesNextMessage`; this asks the same
   * question so the button can say which of the two is about to happen. It read
   * "Send the link" while the parent staged the request silently, so pressing
   * it appeared to do nothing at all.
   */
  deferrable?: boolean;
  /**
   * How to fetch the folders an incoming file may be filed into.
   *
   * ⚠️ **A callback, because the two callers are gated differently.** An open
   * conversation asks `/threads/:id/folders`, which admits anybody *on the
   * thread*; one being started asks `/threads/record-folders`, which can only
   * ask about the record. Choosing one endpoint here would mean one gate for
   * both, and the record-keyed one refuses a vendor reading a conversation they
   * are legitimately on.
   *
   * ⚠️ **A callback rather than the data, so the walk is lazy.** It was a fetch
   * on mount in the parent, which meant every conversation anybody *opened*
   * walked the record's folder tree — one `tree()` call per node, up to two
   * hundred — to fill a dialog most readers never open. It now runs on first
   * open of that dialog and is remembered.
   *
   * ⚠️ **These are library nodes** (`folder:<uuid>`, the record's own node),
   * submitted as they are. See `ThreadFolderOption`.
   */
  loadFolders?: () => Promise<ThreadFolderOption[]>;
}

export function ConversationToolbar({
  context,
  onIntent,
  disabled = false,
  pending = false,
  deferrable = false,
  loadFolders,
}: ConversationToolbarProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Upload-link state, held here so reopening the dialog keeps what was typed.
  const [uploadConfig, setUploadConfig] = useState<UploadLinkConfig>({});
  const [note, setNote] = useState('');

  const inert = busy || disabled;

  /*
   * What the button is about to do, asked of the rule that will decide it
   * rather than restated here — a second copy is how a label starts describing
   * the behaviour it used to have.
   */
  const ridesNextMessage = uploadRidesNextMessage({ note }, deferrable);

  /*
   * The record's folders, fetched once the dialog is first opened.
   *
   * `null` is "not asked yet"; the fields below are handed an empty list
   * meanwhile.
   */
  const [folders, setFolders] = useState<ThreadFolderOption[] | null>(null);
  const [foldersLoading, setFoldersLoading] = useState(false);

  /**
   * Which fetcher has already been asked.
   *
   * ⚠️ **A ref, not the `foldersLoading` state, and that distinction cost a
   * debugging round.** With the flag in the dependency array, setting it
   * *re-ran the effect*, whose cleanup then marked the in-flight request stale —
   * so the answer was thrown away the moment it arrived and the picker stayed
   * empty forever. A ref changes no dependency and therefore cancels nothing.
   *
   * ⚠️ **Keyed on the function, so a changed record refetches.** The parents
   * memoise `loadFolders` on the thread or the record; a new identity means a
   * different one, and its result supersedes whatever the previous request
   * answers afterwards.
   */
  const askedFor = useRef<ConversationToolbarProps['loadFolders']>(undefined);

  // A new fetcher means a different record: drop what the last one answered
  // rather than offering its folders for this one.
  useEffect(() => {
    setFolders(null);
  }, [loadFolders]);

  useEffect(() => {
    if (!askOpen || loadFolders === undefined || askedFor.current === loadFolders) return;
    const mine = loadFolders;
    askedFor.current = mine;
    setFoldersLoading(true);

    // No cleanup, deliberately: closing the dialog must not cancel a walk that
    // reopening would then never retry. Staleness is judged by which fetcher is
    // current, which is the question that actually matters.
    void mine()
      .then((resolved) => {
        if (askedFor.current !== mine) return;
        setFolders(resolved);
        setFoldersLoading(false);
      })
      .catch(() => {
        if (askedFor.current !== mine) return;
        // An empty list, not a fall back to the legacy tree: the dialog still
        // files the upload against the record, and the picker offers nothing
        // rather than offering the wrong thing.
        setFolders([]);
        setFoldersLoading(false);
        /*
         * ⚠️ **Forgotten on failure, so reopening tries again.** A success is
         * remembered — that is the whole point of the ref — but remembering a
         * *failure* the same way meant one bad request left the picker empty
         * until the conversation was closed and reopened from scratch. A
         * network blip should cost a retry, not the feature.
         */
        askedFor.current = undefined;
      });
    // Not `.finally`: it would run after the `catch` had already cleared the
    // ref, so its `askedFor.current === mine` guard would fail and the loading
    // flag would stay set forever. Each branch clears its own.
  }, [askOpen, loadFolders]);

  /*
   * The library nodes, in the shape `UploadLinkFields` speaks. `nodeId` is the
   * value submitted as `folderId` — a node id, not a uuid.
   */
  const folderItems = useMemo(
    () =>
      loadFolders === undefined
        ? []
        : (folders ?? []).map((folder) => ({ id: folder.nodeId, path: folder.path })),
    [loadFolders, folders],
  );

  const scope =
    context?.entityType && context.entityId
      ? { entityType: context.entityType, entityId: context.entityId }
      : undefined;

  /**
   * Hand the choice up, and close the dialog only once it was taken.
   *
   * ⚠️ **A rejection leaves the dialog open on purpose.** The parent reports
   * what went wrong; keeping what was typed is what makes the retry one click
   * rather than a re-entry of the whole request.
   */
  async function emit(intent: ConversationToolIntent, done: () => void): Promise<void> {
    setBusy(true);
    try {
      await onIntent(intent);
      done();
    } catch {
      // Reported by the parent, which owns the banner.
    } finally {
      setBusy(false);
    }
  }

  const button =
    'flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-700 border border-gray-200 border-dashed rounded-lg hover:bg-gray-50 disabled:opacity-50';

  return (
    <>
      <div className="flex flex-wrap gap-2" data-testid="conversation-toolbar">
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          disabled={inert}
          className={button}
          data-testid="thread-attach-from-files"
        >
          <FolderOpen size={13} />
          Attach from files
        </button>

        {/*
          Only where there is an event behind the record: every report is an
          event report, and offering them on a conversation about a vendor in
          general — or about nothing at all — would be a button that answers 400.
        */}
        {context?.eventId && (
          <button
            type="button"
            onClick={() => setReportsOpen(true)}
            disabled={inert}
            className={button}
            data-testid="thread-attach-report"
          >
            <FileText size={13} />
            Attach a report
          </button>
        )}

        <button
          type="button"
          onClick={() => setAskOpen(true)}
          disabled={inert}
          className={button}
          data-testid="thread-ask-for-file"
        >
          <Upload size={13} />
          Ask for a file back
        </button>
      </div>

      {/*
        Mounted only while open, not merely hidden: `useFileExplorer` fetches the
        tree as soon as it runs, so an always-mounted picker would load the whole
        library every time somebody opened a conversation to read two lines.
      */}
      {pickerOpen && (
        <FilePickerModal
          open
          onClose={() => setPickerOpen(false)}
          onPick={(files) => {
            if (files.length === 0) {
              setPickerOpen(false);
              return;
            }
            void emit({ fileIds: files.map((file) => file.id) }, () => setPickerOpen(false));
          }}
          {...(context?.nodeId ? { rootNodeId: context.nodeId } : {})}
        />
      )}

      {reportsOpen && context?.eventId && (
        <ThreadReportModal
          open
          onClose={() => setReportsOpen(false)}
          eventId={context.eventId}
          onAttach={(reports: ThreadReportRequest[]) =>
            emit({ reports }, () => setReportsOpen(false))
          }
        />
      )}

      <EntityModal
        open={askOpen}
        onClose={() => setAskOpen(false)}
        title="Ask for a file back"
        size="md"
        footer={
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setAskOpen(false)}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={inert}
              onClick={() =>
                void emit(
                  {
                    upload: {
                      folderId: uploadConfig.folderId ?? null,
                      ...(uploadConfig.fileTypes ? { fileTypes: uploadConfig.fileTypes } : {}),
                      ...(uploadConfig.label ? { label: uploadConfig.label } : {}),
                      ...(uploadConfig.documentType
                        ? { documentType: uploadConfig.documentType }
                        : {}),
                      note,
                    },
                  },
                  () => setAskOpen(false),
                )
              }
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300"
              data-testid="thread-send-upload-link"
            >
              {busy
                ? 'Sending…'
                : pending || ridesNextMessage
                  ? 'Add the request'
                  : 'Send the link'}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          {/*
            Three sentences because three things happen. The middle one is the
            case the old copy had no words for: with nothing typed below, the
            request waits for whatever is written next rather than going now —
            and a producer who was not told that saw a button do nothing.
          */}
          <p className="text-sm text-gray-500" data-testid="thread-upload-explainer">
            {pending
              ? 'Everybody you add to this conversation gets their own single-use link by email once it starts. The conversation records that you asked; the link itself is never posted in it.'
              : ridesNextMessage
                ? 'Write what you are asking for below to send it now, or leave it empty and the request will go out with your next message. Either way everybody gets their own single-use link by email; the link itself is never posted here.'
                : 'This goes out now as a message on the conversation, and everybody on it gets their own single-use link by email. The link itself is never posted here.'}
          </p>

          <MessageComposer
            id="thread-upload-note"
            label="What are you asking for?"
            value={note}
            onChange={setNote}
            rows={3}
            placeholder="Please send the signed contract."
            record={
              context?.entityType && context.entityId
                ? { entityType: context.entityType, entityId: context.entityId }
                : null
            }
          />

          <UploadLinkFields
            config={uploadConfig}
            onConfigChange={setUploadConfig}
            hasRecordTarget={Boolean(scope)}
            folders={folderItems}
            foldersLoading={foldersLoading}
            disabled={inert}
          />
        </div>
      </EntityModal>
    </>
  );
}
